import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Button } from "@components/ui/button"
import { useActivateAccountMutation } from "@apis/accounts/useAccounts"
import type { IActivateAccountRequest } from "@apis/accounts/types"
import { Loader2 } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"
import { Textarea } from "@components/ui/textarea"
import { DatePicker } from "@components/DatePicker"
import ErrorMessage from "@components/ErrorMessage"

type TActivateAccountFormProps = {
  account_id: string
}

export const ActivateAccountForm = ({
  account_id,
}: TActivateAccountFormProps) => {
  const { closeDrawer } = useDrawer()

  const activateAccountMutation = useActivateAccountMutation()

  const form = useForm<IActivateAccountRequest>({
    mode: "onTouched",
    defaultValues: { service_start_date: "", notes: "" },
  })

  const handleSubmit = (formData: IActivateAccountRequest) => {
    activateAccountMutation.mutate(
      {
        account_id: account_id,
        request: {
          service_start_date: formData.service_start_date,
          notes: formData.notes,
        },
      },
      {
        onSuccess: () => {
          toast.success("Successfully updated service start date!")
          closeDrawer()
        },
        onError: (error) =>
          toast.error(`Error updating service start date! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="tw:space-y-6">
        <div className="tw:flex tw:flex-col tw:gap-4">
          <FormField
            control={form.control}
            name="service_start_date"
            rules={{ required: "Service start date is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start Date of Service</FormLabel>
                <FormControl>
                  <DatePicker field={field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Optional..." rows={5} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <ErrorMessage
          message="Note: This will update the account to Active!"
          variant="destructive-secondary"
        />
        <div className="tw:flex tw:gap-6">
          <Button variant="warning" onClick={closeDrawer}>
            Cancel Change
          </Button>
          <Button
            type="submit"
            disabled={
              activateAccountMutation.isPending || !form.formState.isValid
            }
          >
            {activateAccountMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Confirm Change</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
